import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  UserCog, 
  Plus, 
  Mail,
  Phone,
  Shield,
  CheckCircle,
  XCircle,
  Users,
  GitBranch,
  ChevronRight,
  Building2,
  Briefcase,
  Edit,
  MapPin
} from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  super_admin: "bg-red-100 text-red-700",
  head: "bg-purple-100 text-purple-700",
  clinic_admin: "bg-blue-100 text-blue-700",
  clinic_manager: "bg-amber-100 text-amber-700",
  audiologist: "bg-emerald-100 text-emerald-700",
  business_team: "bg-cyan-100 text-cyan-700",
  front_desk: "bg-slate-100 text-slate-700",
  sales_crm: "bg-orange-100 text-orange-700"
};

const DEPARTMENTS = [
  { value: "clinical", label: "Clinical" },
  { value: "sales", label: "Sales & Business" },
  { value: "operations", label: "Operations" },
  { value: "management", label: "Management" }
];

export default function UsersPage() {
  const { user, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [hierarchyDialogOpen, setHierarchyDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "audiologist",
    clinic_id: "",
    phone: ""
  });

  const [editUser, setEditUser] = useState({
    id: null,
    name: "",
    role: "",
    clinic_id: "",
    phone: ""
  });

  const [hierarchyUpdate, setHierarchyUpdate] = useState({
    reports_to: "",
    designation: "",
    department: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clinicsRes, hierarchyRes] = await Promise.all([
        apiClient.get("/users-with-hierarchy"),
        apiClient.get("/clinics"),
        apiClient.get("/hierarchy/tree")
      ]);
      setUsers(usersRes.data);
      setClinics(clinicsRes.data);
      setHierarchy(hierarchyRes.data.hierarchy || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // Fallback to basic user fetch
      try {
        const [usersRes, clinicsRes] = await Promise.all([
          apiClient.get("/users"),
          apiClient.get("/clinics")
        ]);
        setUsers(usersRes.data);
        setClinics(clinicsRes.data);
      } catch (e) {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateHierarchy = async () => {
    if (!selectedUser) return;
    try {
      await apiClient.put(`/users/${selectedUser.id}/hierarchy`, hierarchyUpdate);
      toast.success("User hierarchy updated!");
      setHierarchyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update hierarchy");
    }
  };

  const openEditDialog = (u) => {
    setEditUser({
      id: u.id,
      name: u.name || "",
      role: u.role,
      clinic_id: u.clinic_id || "",
      phone: u.phone || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
        clinic_id: editUser.clinic_id || null,
        phone: editUser.phone
      });
      toast.success("User updated successfully!");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const openHierarchyDialog = (u) => {
    setSelectedUser(u);
    setHierarchyUpdate({
      reports_to: u.reports_to || "",
      designation: u.designation || "",
      department: u.department || ""
    });
    setHierarchyDialogOpen(true);
  };

  const renderHierarchyNode = (node, depth = 0) => {
    return (
      <div key={node.user_id} className="ml-4 border-l-2 border-slate-200 pl-4">
        <div className={`p-3 rounded-lg ${depth === 0 ? 'bg-indigo-50' : 'bg-white border'} mb-2`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">{node.user_name}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={ROLE_COLORS[node.role] || "bg-slate-100"}>{node.role}</Badge>
                {node.designation && <span className="text-xs text-slate-500">{node.designation}</span>}
              </div>
            </div>
            {node.children?.length > 0 && (
              <Badge variant="outline">{node.children.length} direct reports</Badge>
            )}
          </div>
        </div>
        {node.children?.map(child => renderHierarchyNode(child, depth + 1))}
      </div>
    );
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post("/auth/register", newUser);
      setUsers([response.data, ...users]);
      toast.success("User created successfully!");
      setDialogOpen(false);
      setNewUser({
        name: "",
        email: "",
        password: "",
        role: "audiologist",
        clinic_id: clinics[0]?.id || "",
        phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await apiClient.put(`/users/${userId}/status?is_active=${!currentStatus}`);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_active: !currentStatus } : u
      ));
      toast.success(`User ${!currentStatus ? "activated" : "deactivated"}`);
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const getRoleBadge = (role) => {
    return <Badge className={ROLE_COLORS[role] || "bg-slate-100"}>{role?.replace("_", " ")}</Badge>;
  };

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic?.name || "All Clinics";
  };

  const roles = [
    { value: "front_desk", label: "Front Desk" },
    { value: "audiologist", label: "Audiologist" },
    { value: "business_team", label: "Business Team" },
    { value: "clinic_manager", label: "Clinic Manager" },
    { value: "clinic_admin", label: "Clinic Admin" },
    { value: "head", label: "Head" }
  ];

  if (isSuperAdmin) {
    roles.unshift({ value: "super_admin", label: "Super Admin" });
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">User Management</h1>
            <p className="text-slate-500">Manage platform users and permissions</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
                <DialogDescription>Create a new user account</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="Dr. John Doe"
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="john@hearclear.in"
                    required
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Role *</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                      placeholder="+91-9876543210"
                    />
                  </div>
                </div>
                {newUser.role !== "super_admin" && (
                  <div>
                    <Label>Clinic *</Label>
                    <Select value={newUser.clinic_id} onValueChange={(v) => setNewUser({...newUser, clinic_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select clinic" />
                      </SelectTrigger>
                      <SelectContent>
                        {clinics.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-700">
                    Create User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {["super_admin", "clinic_admin", "audiologist", "sales_crm"].map(role => {
            const count = users.filter(u => u.role === role).length;
            const labels = {
              super_admin: "Super Admins",
              clinic_admin: "Clinic Admins",
              audiologist: "Audiologists",
              sales_crm: "Sales / CRM"
            };
            return (
              <Card key={role}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-500">{labels[role]}</p>
                  </div>
                  {getRoleBadge(role)}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton h-20 rounded-lg"></div>
                ))}
              </div>
            ) : users.length > 0 ? (
              <div className="space-y-3">
                {users.map(u => (
                  <div 
                    key={u.id}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`user-card-${u.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                        <span className="text-sky-700 font-semibold text-lg">
                          {u.name?.charAt(0) || "U"}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{u.name}</p>
                          {!u.is_active && (
                            <Badge variant="outline" className="text-red-600 border-red-200">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </span>
                          {u.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {u.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {getRoleBadge(u.role)}
                        <p className="text-xs text-slate-400 mt-1">
                          {getClinicName(u.clinic_id)}
                        </p>
                      </div>
                      {u.id !== user.id && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(u)}
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleUserStatus(u.id, u.is_active)}
                            className={u.is_active ? "text-red-600" : "text-green-600"}
                          >
                            {u.is_active ? (
                              <>
                                <XCircle className="w-4 h-4 mr-1" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Activate
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No users found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user details and clinic assignment</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={editUser.name}
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                  placeholder="Dr. John Doe"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select 
                    value={editUser.role} 
                    onValueChange={(v) => setEditUser({...editUser, role: v})}
                    disabled={!isSuperAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isSuperAdmin && (
                    <p className="text-xs text-slate-400 mt-1">Only Super Admin can change roles</p>
                  )}
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editUser.phone}
                    onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                    placeholder="+91-9876543210"
                  />
                </div>
              </div>
              {editUser.role !== "super_admin" && (
                <div>
                  <Label>Assigned Clinic</Label>
                  <Select 
                    value={editUser.clinic_id || "all"} 
                    onValueChange={(v) => setEditUser({...editUser, clinic_id: v === "all" ? "" : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select clinic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clinics (No specific assignment)</SelectItem>
                      {clinics.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            {c.name}
                            {c.city && <span className="text-xs text-slate-400">({c.city})</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Reassign user to a different clinic
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="save-user-btn">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
